// hooks/useDraggable.js
import { useEffect, useState, useRef } from 'react';

export default function useDraggable(ref) {
    const [dragging, setDragging] = useState(false);
    const [isDragged, setIsDragged] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const offset = useRef({ x: 0, y: 0 });
    const rafId = useRef(null);

    const onMouseDown = (e) => {
        if (e.button !== 0 || !ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        offset.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        };

        // The modal starts centered by flex layout. Convert it to viewport
        // coordinates before dragging so the first movement does not jump.
        ref.current.style.position = 'fixed';
        ref.current.style.left = `${rect.left}px`;
        ref.current.style.top = `${rect.top}px`;
        ref.current.style.width = `${rect.width}px`;
        ref.current.style.transform = 'none';

        if (!isDragged) {
            setPosition({ x: rect.left, y: rect.top });
            setIsDragged(true);
        }
        setDragging(true);
        e.preventDefault();
    };

    useEffect(() => {
        if (!dragging || !ref.current) return;

        const handleMouseMove = (e) => {
            if (rafId.current) cancelAnimationFrame(rafId.current);
            rafId.current = requestAnimationFrame(() => {
                if (!ref.current) return;
                const newX = e.clientX - offset.current.x;
                const newY = e.clientY - offset.current.y;
                ref.current.style.left = `${newX}px`;
                ref.current.style.top = `${newY}px`;
                ref.current.style.transform = 'none'; // Override transform during drag
            });
        };

        const handleMouseUp = () => {
            if (rafId.current) cancelAnimationFrame(rafId.current);
            setDragging(false);
            if (!ref.current) return;
            const rect = ref.current.getBoundingClientRect();
            setPosition({ x: rect.left, y: rect.top });
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = 'none';
        if (ref.current) ref.current.style.transition = 'none'; // Disable transitions during drag

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = '';
            if (ref.current) ref.current.style.transition = ''; // Restore transitions
            if (rafId.current) cancelAnimationFrame(rafId.current);
        };
    }, [dragging, ref]);

    const reset = () => {
        setIsDragged(false);
        setPosition({ x: 0, y: 0 });
        if (ref.current) {
            ref.current.style.position = '';
            ref.current.style.left = '';
            ref.current.style.top = '';
            ref.current.style.width = '';
            ref.current.style.transform = '';
        }
    };

    return { position, isDragged, onMouseDown, reset };
}